import { Spinner } from "@base/ui/Spinner";
import { ChevronDown } from "lucide-react";
import { PULL_THRESHOLD_PX } from "./usePullToRefresh";

const INDICATOR_SIZE_PX = 40;
const INDICATOR_TOP_OFFSET_PX = 12;
const INDICATOR_VISIBLE_OFFSET_PX = INDICATOR_SIZE_PX + INDICATOR_TOP_OFFSET_PX;

const RING_RADIUS = 18.5;
const RING_STROKE_WIDTH = 3;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const RING_GRADIENT_START = "#A78BFA"; // violet-400
const RING_GRADIENT_END = "#60A5FA"; // blue-400
const RING_TRACK_COLOR = "#ede9fe"; // violet-50
const SPINNER_COLOR = "#818CF8"; // indigo-400

const CHEVRON_COLOR_FROM = { r: 148, g: 163, b: 184 }; // slate-400
const CHEVRON_COLOR_TO = { r: 129, g: 140, b: 248 }; // indigo-400

function interpolateChevronColor(progress: number): string {
  const t = Math.min(1, progress);
  const r = Math.round(CHEVRON_COLOR_FROM.r + (CHEVRON_COLOR_TO.r - CHEVRON_COLOR_FROM.r) * t);
  const g = Math.round(CHEVRON_COLOR_FROM.g + (CHEVRON_COLOR_TO.g - CHEVRON_COLOR_FROM.g) * t);
  const b = Math.round(CHEVRON_COLOR_FROM.b + (CHEVRON_COLOR_TO.b - CHEVRON_COLOR_FROM.b) * t);
  return `rgb(${r},${g},${b})`;
}

interface PullToRefreshIndicatorProps {
  pullDistance: number;
  isRefreshing: boolean;
  isPulling: boolean;
}

function CircularProgress({ progress }: { progress: number }) {
  const strokeDashoffset = RING_CIRCUMFERENCE * (1 - progress);
  const gradientId = "ptr-ring-gradient";
  const cx = INDICATOR_SIZE_PX / 2;
  const cy = INDICATOR_SIZE_PX / 2;

  return (
    <svg
      width={INDICATOR_SIZE_PX}
      height={INDICATOR_SIZE_PX}
      viewBox={`0 0 ${INDICATOR_SIZE_PX} ${INDICATOR_SIZE_PX}`}
      className="absolute inset-0"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={RING_GRADIENT_START} />
          <stop offset="100%" stopColor={RING_GRADIENT_END} />
        </linearGradient>
      </defs>
      <circle cx={cx} cy={cy} r={RING_RADIUS} fill="none" stroke={RING_TRACK_COLOR} strokeWidth={RING_STROKE_WIDTH} />
      <circle
        cx={cx}
        cy={cy}
        r={RING_RADIUS}
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeWidth={RING_STROKE_WIDTH}
        strokeLinecap="round"
        strokeDasharray={RING_CIRCUMFERENCE}
        strokeDashoffset={strokeDashoffset}
        transform={`rotate(-90 ${cx} ${cy})`}
        style={{ transition: "stroke-dashoffset 0.05s linear" }}
      />
    </svg>
  );
}

function ChevronIcon({ progress }: { progress: number }) {
  const isReady = progress >= 1;
  const color = interpolateChevronColor(progress);

  return (
    <ChevronDown
      size={20}
      aria-hidden
      strokeWidth={3}
      style={{
        display: "block",
        color,
        transform: `rotate(${isReady ? 180 : 0}deg) scale(${isReady ? 1.1 : 1})`,
        transition: isReady
          ? "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), color 0.15s ease"
          : "transform 0.2s ease, color 0.08s linear",
      }}
    />
  );
}

export function PullToRefreshIndicator({ pullDistance, isRefreshing, isPulling }: PullToRefreshIndicatorProps) {
  const progress = Math.min(1, pullDistance / PULL_THRESHOLD_PX);
  const translateY = pullDistance - INDICATOR_VISIBLE_OFFSET_PX;
  const opacity = Math.min(1, progress * 1.5);

  const isSettling = !isPulling && !isRefreshing;

  return (
    <div
      className="absolute left-0 right-0 flex justify-center z-20 pointer-events-none"
      style={{ top: INDICATOR_TOP_OFFSET_PX }}
    >
      <div
        style={{
          transform: isRefreshing ? "translateY(0px)" : `translateY(${translateY}px)`,
          opacity: isRefreshing ? 1 : opacity,
          transition:
            isSettling || isRefreshing
              ? "transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.35s ease"
              : "none",
          width: INDICATOR_SIZE_PX,
          height: INDICATOR_SIZE_PX,
          position: "relative",
        }}
      >
        <div className="absolute inset-0 rounded-full bg-white shadow-lg shadow-black/10" />
        {isRefreshing ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <Spinner size={20} color={SPINNER_COLOR} />
          </div>
        ) : (
          <>
            <CircularProgress progress={progress} />
            <div className="absolute inset-0 flex items-center justify-center">
              <ChevronIcon progress={progress} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
